// Pure client-side generators (passwords, UUIDs, lorem ipsum, random numbers
// and strings). Randomness uses the Web Crypto API where it matters (passwords,
// UUIDs, strings) so output is cryptographically strong, never server-generated.

export type GeneratorVariant =
  | 'password-generator'
  | 'uuid-generator'
  | 'lorem-ipsum-generator'
  | 'random-number-generator'
  | 'random-string-generator'
  | 'dice-roller'
  | 'coin-flipper'
  | 'random-letter-generator'
  | 'random-word-generator';

function randomInt(maxExclusive: number): number {
  if (maxExclusive <= 0) return 0;
  const array = new Uint32Array(1);
  // Reject values in the biased tail so the distribution stays uniform.
  const limit = Math.floor(0xffffffff / maxExclusive) * maxExclusive;
  let value = 0;
  do {
    crypto.getRandomValues(array);
    value = array[0];
  } while (value >= limit);
  return value % maxExclusive;
}

function pick(pool: string): string {
  return pool[randomInt(pool.length)];
}

export type PasswordOptions = {
  length: number;
  lowercase: boolean;
  uppercase: boolean;
  numbers: boolean;
  symbols: boolean;
};

const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DIGITS = '0123456789';
const SYMBOLS = '!@#$%^&*()-_=+[]{};:,.?/';

export function generatePassword(options: PasswordOptions): string {
  const length = Math.min(Math.max(Math.floor(options.length) || 0, 4), 128);
  const pools: string[] = [];
  if (options.lowercase) pools.push(LOWER);
  if (options.uppercase) pools.push(UPPER);
  if (options.numbers) pools.push(DIGITS);
  if (options.symbols) pools.push(SYMBOLS);
  if (!pools.length) {
    throw new Error('Select at least one character type.');
  }
  const all = pools.join('');
  // Guarantee at least one character from each selected pool, then fill the rest.
  const chars = pools.map((pool) => pick(pool));
  for (let i = chars.length; i < length; i += 1) {
    chars.push(pick(all));
  }
  // Fisher-Yates shuffle so the guaranteed characters are not always first.
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.slice(0, length).join('');
}

export function generateUuid(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
    .slice(6, 8)
    .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

export function generateUuids(count: number): string {
  const total = Math.min(Math.max(Math.floor(count) || 1, 1), 100);
  return Array.from({ length: total }, () => generateUuid()).join('\n');
}

const LOREM_WORDS =
  'lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua enim ad minim veniam quis nostrud exercitation ullamco laboris nisi aliquip ex ea commodo consequat duis aute irure in reprehenderit voluptate velit esse cillum fugiat nulla pariatur excepteur sint occaecat cupidatat non proident sunt culpa qui officia deserunt mollit anim id est laborum'.split(
    ' ',
  );

export function generateLoremIpsum(paragraphs: number): string {
  const total = Math.min(Math.max(Math.floor(paragraphs) || 1, 1), 20);
  const blocks: string[] = [];
  for (let p = 0; p < total; p += 1) {
    const sentenceCount = 3 + randomInt(3);
    const sentences: string[] = [];
    for (let s = 0; s < sentenceCount; s += 1) {
      const wordCount = 8 + randomInt(9);
      const words = Array.from(
        { length: wordCount },
        () => LOREM_WORDS[randomInt(LOREM_WORDS.length)],
      );
      let sentence = words.join(' ');
      sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1) + '.';
      sentences.push(sentence);
    }
    let block = sentences.join(' ');
    if (p === 0)
      block = `Lorem ipsum dolor sit amet, ${block.charAt(0).toLowerCase()}${block.slice(1)}`;
    blocks.push(block);
  }
  return blocks.join('\n\n');
}

export function generateRandomNumbers(min: number, max: number, count: number): string {
  const lo = Math.floor(Number.isFinite(min) ? min : 1);
  const hi = Math.floor(Number.isFinite(max) ? max : 100);
  if (hi < lo) {
    throw new Error('The maximum must be greater than or equal to the minimum.');
  }
  const total = Math.min(Math.max(Math.floor(count) || 1, 1), 1000);
  const span = hi - lo + 1;
  return Array.from({ length: total }, () => String(lo + randomInt(span))).join('\n');
}

export function generateRandomString(length: number, includeSymbols: boolean): string {
  const total = Math.min(Math.max(Math.floor(length) || 0, 1), 256);
  const pool = LOWER + UPPER + DIGITS + (includeSymbols ? SYMBOLS : '');
  return Array.from({ length: total }, () => pick(pool)).join('');
}

export function rollDice(count: number, sides: number): string {
  const total = Math.min(Math.max(Math.floor(count) || 1, 1), 20);
  const faces = Math.min(Math.max(Math.floor(sides) || 6, 2), 100);
  const rolls = Array.from({ length: total }, () => 1 + randomInt(faces));
  const sum = rolls.reduce((acc, n) => acc + n, 0);
  return `Rolls: ${rolls.join(', ')}\nTotal: ${sum}`;
}

export function flipCoins(count: number): string {
  const total = Math.min(Math.max(Math.floor(count) || 1, 1), 100);
  const flips = Array.from({ length: total }, () => (randomInt(2) === 0 ? 'Heads' : 'Tails'));
  const heads = flips.filter((f) => f === 'Heads').length;
  return `${flips.join(', ')}\nHeads: ${heads}  Tails: ${total - heads}`;
}

export function generateRandomLetters(count: number): string {
  const total = Math.min(Math.max(Math.floor(count) || 1, 1), 200);
  return Array.from({ length: total }, () => pick(UPPER)).join(' ');
}

const RANDOM_WORDS =
  'able acid apple beach brave bread bright brush cable candle cheer clever cloud coral crisp dance delta ember fable feather flame forest garden gentle glass grape harbor hazel honey island ivory jelly jolly kernel kettle ladder lemon lunar maple marble meadow nectar noble ocean olive orbit pearl pebble pepper piano prism quartz quiet quill raven ripple river robin saddle silver spark spruce stone sugar summer sunny table thunder timber tulip umber unity valley velvet violet walnut willow window winter wonder yellow zephyr zigzag'.split(
    ' ',
  );

export function generateRandomWords(count: number): string {
  const total = Math.min(Math.max(Math.floor(count) || 1, 1), 100);
  return Array.from({ length: total }, () => RANDOM_WORDS[randomInt(RANDOM_WORDS.length)]).join(
    ' ',
  );
}
